import { useRef, useEffect, useState } from 'react';
import Editor, { useMonaco } from '@monaco-editor/react';
import {
  Save,
  Copy,
  Check,
  Undo,
  Redo,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/utils/cn';

interface CodeViewerProps {
  path: string;
  content: string;
  language?: string;
  readOnly?: boolean;
  onChange?: (content: string) => void;
  onSave?: (content: string) => void;
  onClose?: () => void;
  className?: string;
}

export function CodeViewer({
  path,
  content,
  language,
  readOnly = false,
  onChange,
  onSave,
  onClose,
  className,
}: CodeViewerProps) {
  const { theme } = useTheme();
  const monaco = useMonaco();
  const editorRef = useRef<any>(null);
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fileName = path.split('/').pop() || 'untitled';
  const detectedLanguage = language || getLanguageFromPath(path);

  // Configure Monaco editor
  useEffect(() => {
    if (monaco) {
      // Set editor theme
      monaco.editor.defineTheme('spark-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [],
        colors: {
          'editor.background': '#1a1a2e',
          'editor.foreground': '#e4e4e7',
          'editorLineNumber.foreground': '#6b7280',
          'editorLineNumber.activeForeground': '#a1a1aa',
          'editor.selectionBackground': '#3730a3',
          'editor.lineHighlightBackground': '#27273a',
        },
      });

      monaco.editor.defineTheme('spark-light', {
        base: 'vs',
        inherit: true,
        rules: [],
        colors: {
          'editor.background': '#ffffff',
          'editor.foreground': '#1f2937',
        },
      });
    }
  }, [monaco]);

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;

    // Add keyboard shortcuts
    if (monaco?.KeyMod?.CtrlCmd && monaco?.KeyCode?.KeyS) {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        handleSave();
      });
    }
  };

  const handleContentChange = (value: string | undefined) => {
    if (value !== undefined) {
      setHasChanges(value !== content);
      onChange?.(value);
    }
  };

  const handleSave = () => {
    if (editorRef.current && onSave) {
      const value = editorRef.current.getValue();
      onSave(value);
      setHasChanges(false);
    }
  };

  const handleCopy = async () => {
    const value = editorRef.current?.getValue() || content;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUndo = () => {
    editorRef.current?.trigger('keyboard', 'undo', null);
  };

  const handleRedo = () => {
    editorRef.current?.trigger('keyboard', 'redo', null);
  };

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-white dark:bg-dark-800',
        isFullscreen && 'fixed inset-0 z-50',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {fileName}
          </span>
          {hasChanges && (
            <span className="w-2 h-2 rounded-full bg-yellow-500" title="Unsaved changes" />
          )}
          <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded bg-gray-200 dark:bg-dark-700">
            {detectedLanguage}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {!readOnly && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUndo}
                title="Undo (Ctrl+Z)"
              >
                <Undo className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRedo}
                title="Redo (Ctrl+Y)"
              >
                <Redo className="w-4 h-4" />
              </Button>
              <div className="w-px h-4 bg-gray-300 dark:bg-dark-600 mx-1" />
            </>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>

          {!readOnly && onSave && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges}
              title="Save (Ctrl+S)"
            >
              <Save className="w-4 h-4" />
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>

          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} title="Close">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <Editor
          height="100%"
          defaultLanguage={detectedLanguage}
          language={detectedLanguage}
          value={content}
          onChange={handleContentChange}
          onMount={handleEditorDidMount}
          theme={theme === 'dark' ? 'spark-dark' : 'spark-light'}
          options={{
            readOnly,
            minimap: { enabled: false },
            fontSize: 13,
            fontFamily: 'JetBrains Mono, Menlo, Monaco, monospace',
            lineNumbers: 'on',
            renderLineHighlight: 'line',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            padding: { top: 16, bottom: 16 },
            scrollbar: {
              verticalScrollbarSize: 8,
              horizontalScrollbarSize: 8,
            },
          }}
        />
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-1 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
        <span>{path}</span>
        <div className="flex items-center gap-4">
          <span>UTF-8</span>
          <span>{detectedLanguage.toUpperCase()}</span>
        </div>
      </div>
    </div>
  );
}

function getLanguageFromPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase();

  const languageMap: Record<string, string> = {
    js: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    json: 'json',
    html: 'html',
    htm: 'html',
    css: 'css',
    scss: 'scss',
    less: 'less',
    md: 'markdown',
    py: 'python',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    java: 'java',
    kt: 'kotlin',
    swift: 'swift',
    php: 'php',
    sql: 'sql',
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',
    yaml: 'yaml',
    yml: 'yaml',
    xml: 'xml',
    dockerfile: 'dockerfile',
    env: 'dotenv',
    gitignore: 'ignore',
  };

  return languageMap[ext || ''] || 'plaintext';
}
